import { Mail, MessageSquareText, PhoneCall } from 'lucide-react';

import { FILL_INS, SOURCES, SOURCE_FILL_IN, fill, smsSegments, unfilled, unknownFillIns, usesFillIn } from '../../../lib/messageCore';

// Shared pieces of the Messages page (Nic 2026-10-22).

export const CARD =
  'rounded-2xl bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-surface-card dark:ring-1 dark:ring-white/10';

export const CHANNEL = {
  text: { label: 'Text', plural: 'Texts', Icon: MessageSquareText, tint: 'bg-sky-500' },
  email: { label: 'Email', plural: 'Emails', Icon: Mail, tint: 'bg-indigo-500' },
  call: { label: 'Call script', plural: 'Call scripts', Icon: PhoneCall, tint: 'bg-amber-500' },
};

// The five sources, short, for tabs and chips.
export const SOURCE_SHORT = { partner: 'Partner', event: 'Event', website: 'Website', referral: 'Referred', called: 'Called us' };

export const OWN_KEY_RE = /^(partner|event):(.+)$/;

/** The words for one wording's name: "Partner", "VeteranRelief.org", ... */
export const wordingLabel = (key, partners = []) => {
  if (key === 'all') return 'Everyone';
  const own = OWN_KEY_RE.exec(key);
  if (own) return partners.find((p) => p.partnerId === own[2])?.partnerName || 'Removed partner';
  return SOURCE_SHORT[key] || key;
};

/** Which source a wording is for ('all' for one wording). */
export const kindOf = (key) => (key === 'all' ? null : key.split(':')[0]);

/** The fill-ins one wording may use: the message's own, plus its source's name. */
export const fillInsAllowed = (message, key) => {
  const own = new Set(message.fillIns || []);
  const kind = kindOf(key);
  if (kind && SOURCE_FILL_IN[kind]) own.add(SOURCE_FILL_IN[kind]);
  return FILL_INS.filter((f) => own.has(f.key));
};

// Who reads it in the preview, per source (made-up names; a partner's or
// event's own wording uses the name their leads really arrive with).
const SAMPLE_NAME = { partner: 'Marcus Bennett', event: 'Veterans Expo Tampa', referral: 'Lena Reyes' };
export const sampleFor = (key, partners = []) => {
  const kind = kindOf(key);
  const own = OWN_KEY_RE.exec(key);
  const ownPartner = own ? partners.find((p) => p.partnerId === own[2]) : null;
  const name = own ? ownPartner?.sayName || ownPartner?.partnerName || 'that partner' : SAMPLE_NAME[kind] || null;
  const values = Object.fromEntries(FILL_INS.map((f) => [f.key, f.sample]));
  // Their own source's name only; other sources' names stay empty.
  ['partner_name', 'event_name', 'referrer_name'].forEach((k) => {
    delete values[k];
  });
  if (kind && SOURCE_FILL_IN[kind]) values[SOURCE_FILL_IN[kind]] = name;
  values.source_name = kind === 'website' ? 'our website' : kind === 'called' ? 'your call with our team' : name;
  const who = {
    partner: own ? `a lead from ${ownPartner?.partnerName || name}` : 'a lead from a partner',
    event: own ? `a lead who signed up at ${name}` : 'a lead from an event',
    website: 'a lead from our website',
    referral: `a lead ${name} referred`,
    called: 'a lead who called us',
  }[kind];
  return { values, who: who || 'anyone' };
};

/** One wording, as its sample person reads it. */
export const readWording = (wording, key, partners, channel) => {
  const { values, who } = sampleFor(key, partners);
  const body = fill(wording?.body || '', values).replace(/ +,/g, ',').trim();
  const subject = wording?.subject ? fill(wording.subject, values).trim() : null;
  return { who, body, subject, stuck: unfilled(`${subject || ''}\n${body}`), ...(channel === 'text' ? smsSegments(body) : { length: body.length }) };
};

/** Every wording key a message must have, plus its own ones. */
export const wordingKeys = (message) =>
  message.mode === 'one' ? ['all'] : [...SOURCES, ...Object.keys(message.wordings || {}).filter((k) => OWN_KEY_RE.test(k))];

/**
 * What the page checks as you type (the server checks again on save):
 * { byKey: { key: [errors] }, errors, warnings }.
 */
export const checkMessage = (message, { limits = {}, partners = [] } = {}) => {
  const byKey = {};
  const warnings = [];
  const add = (key, msg) => {
    byKey[key] = [...(byKey[key] || []), msg];
  };
  const max = limits[message.channel] || 1000;
  const general = [];
  if (!String(message.name || '').trim()) general.push('Give it a name.');
  wordingKeys(message).forEach((key) => {
    const w = message.wordings?.[key] || {};
    const label = wordingLabel(key, partners);
    const body = String(w.body || '');
    if (!body.trim()) add(key, 'Needs words.');
    if (body.length > max) add(key, `Over ${max} characters.`);
    if (message.channel === 'email' && !String(w.subject || '').trim()) add(key, 'Needs a subject.');
    const text = `${w.subject || ''}\n${body}`;
    const allowed = new Set(fillInsAllowed(message, key).map((f) => f.key));
    FILL_INS.filter((f) => !allowed.has(f.key) && usesFillIn(text, f.key)).forEach((f) =>
      add(key, `{${f.label}} can't fill in here.`),
    );
    (message.required || []).forEach((rk) => {
      if (!usesFillIn(body, rk)) add(key, `Keep {${FILL_INS.find((f) => f.key === rk)?.label || rk}}: this draft sends it.`);
    });
    unknownFillIns(text)
      .filter((t) => !/\{\s*referral\s+source\s*\}/i.test(t))
      .forEach((t) => warnings.push(`${label}: ${t} isn't a fill-in, so it sends as typed.`));
  });
  const errors = [...general, ...Object.entries(byKey).flatMap(([k, list]) => list.map((m) => `${wordingLabel(k, partners)}: ${m}`))];
  return { byKey, errors, warnings: [...new Set(warnings)] };
};

/** The fields a save sends. */
export const fieldsOf = (m) => ({
  name: m?.name || '',
  channel: m?.channel || 'text',
  mode: m?.mode || 'one',
  wordings: JSON.parse(JSON.stringify(m?.wordings || {})),
});

export const sameFields = (a, b) => JSON.stringify(fieldsOf(a)) === JSON.stringify(fieldsOf(b));

/** Where a message is used, as one line. */
export const usedLine = (m) => {
  if (!m.uses?.length) return 'Not used by any step yet';
  if (m.kind !== 'cadence' && m.kind !== 'cadence_draft') return m.uses[0].label;
  return m.uses.map((u) => `${u.label}${u.automated ? ' (automated)' : ''}`).join(' · ');
};
